import 'simplebar/dist/simplebar.min.css';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSession, setSession } from '../../utils/session';
import { consentAPI, authAPI } from '../../services/redwolfSdkAPI';
import { appConfig } from '../../utils/appConfig';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import Modal from '../common/components/Modal';
import Spinner from '../common/components/Spinner';
import * as Sentry from '@sentry/react';
import { setEmailPreferenceStatus, setTermsOfServiceStatus } from '../../store/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import PublicContainer from '../containers/PublicContainer';
import useMobileView from '../common/hooks/useMobileView';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import { getLocalStorage, getToken } from '../../utils/localStorage';
import useRedirection from '../common/hooks/useRedirection';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel } from '@reach/accordion';
import useLogout from '../common/hooks/useLogout';

interface ConsentMessageData {
	termsOfServiceText: string;
	privacyPolicyText: string;
	checkBoxText: string;
}

const TermsOfService = () => {
	const [acceptTerms, setAcceptTermsofUse] = useState(false);
	const [acceptTermsPolicy, setAcceptTermsPolicy] = useState(false);
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [isMobileView] = useMobileView();
	const [selectedTab, setSelectedTab] = useState('TERMS_OF_USE');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [handleUserLogout] = useLogout();
	const [consentMessage, setConsentMessage] = useState<ConsentMessageData>({
		termsOfServiceText: '',
		privacyPolicyText: '',
		checkBoxText: ''
	});

	const queryStrings = new URLSearchParams(useLocation().search);
	const redirectURLFromQueryString = queryStrings.get('redirect_url');
	const { termsOfServiceStatus } = useSelector((state: any) => state.user);
	const token = getToken();
	const [userRedirection] = useRedirection();

	const transaction = Sentry.startInactiveSpan({ name: 'Terms Of Service' });

	const termsPopup = document.getElementsByClassName('tos-modal')[0];
	if (termsPopup) {
		termsPopup.classList.remove('modal_pop');
	}

	useEffect(() => {
		const simplebarContentWrapper = document.querySelector('.simplebar-content-wrapper');
		const simplebarScrollBlock = document.querySelector('.scroll-block');
		if (simplebarContentWrapper && simplebarScrollBlock) {
			simplebarContentWrapper.setAttribute('aria-label', 'Terms of Service scrollable content');
			simplebarContentWrapper.setAttribute('tabindex', '0');
			simplebarContentWrapper.addEventListener('focus', () => {
				simplebarScrollBlock.classList.add('border-focus');
			});
			simplebarContentWrapper.addEventListener('blur', () => {
				simplebarScrollBlock.classList.remove('border-focus');
			});
		}
		if (simplebarScrollBlock) {
			simplebarScrollBlock.removeAttribute('tabindex');
		}

		consentMessageRequest();

		let mainContainer: any = null;

		setTimeout(() => {
			mainContainer = document.getElementById('main-container');

			if (mainContainer) {
				mainContainer.classList.add('accout-login');
			}
		}, 0);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleContinue = () => {
		if (acceptTerms && acceptTermsPolicy) {
			saveUserConsentRequest('ACCEPT');
		}
	};

	const handleSetTermsofUse = (checked: boolean) => {
		setAcceptTermsofUse(checked);
	};
	const handleSetTerms = (checked: boolean) => {
		setAcceptTermsPolicy(checked);
	};

	const handleModalClose = () => {
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_TOS_DISMISS');
		setShowDeclineModal(false);
	};

	const handleDecline = () => {
		saveUserConsentRequest('REJECT');
	};

	const consentMessageRequest = () => {
		const userConsentDataPayload = {
			consentIds:
				termsOfServiceStatus === 'POLICY_CHANGE'
					? appConfig.redirectConsentIds
					: appConfig.consentIds,
			consentGroupIds: appConfig.consentGroupIds,
			acceptLanguage: appConfig.acceptLanguage,
			consentMessageFormat: appConfig.consentMessageFormat,
			expand: true,
			accessToken: token
		};

		requestRedwolfService(consentAPI.getConsentData(userConsentDataPayload))
			.then((response: any) => {
				setIsSpinnerLoading(false);

				const result = response.constructor.$metadata$.simpleName;

				if (result === 'Success') {
					setSession('consentToken', response.data.consentToken);

					const consentClientData = response.data.consentClientData.toArray();
					const consentGroups = response.data.consentGroups.toArray();

					const consentMessageData: ConsentMessageData = {
						privacyPolicyText: '',
						termsOfServiceText: '',
						checkBoxText: ''
					};

					if (consentClientData.length) {
						consentClientData.forEach((clientData: any) => {
							if (clientData.id === 'ACCOUNT_TERMS_OF_SERVICE') {
								consentMessageData.termsOfServiceText = clientData.consentContent.textInfo.content;
							} else if (clientData.id === 'PRIVACY_POLICY') {
								consentMessageData.privacyPolicyText = clientData.consentContent.textInfo.content;
							}
						});
					}

					if (consentGroups.length) {
						consentGroups.forEach((clientData: any) => {
							if (clientData.id === 'VIZIO_ACCOUNT_GROUP') {
								consentMessageData.checkBoxText = clientData.title;
							}
						});
					}

					setConsentMessage(consentMessageData);
				} else {
					showTechnicalErrorMessage();
					Sentry.captureException(new Error('requestConsentMessage:' + response));
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				transaction && transaction.end();
				Sentry.captureException(err);
			});
	};

	const saveUserConsentRequest = (newConsentAction: any) => {
		setIsSpinnerLoading(true);

		const sendSMS = getLocalStorage('isSMSConsentChecked');
		const getUserProfile = getLocalStorage('userProfile');
		const saveUserConsentRequestPayload: any = {
			userId: getUserProfile && getUserProfile.userId,
			userIdType: appConfig.userIdType,
			consentIdsActions: {
				acceptList: [],
				rejectList: []
			},
			consentGroupIdsActions: {
				acceptList: [],
				rejectList: []
			},
			consentToken: getSession('consentToken'),
			accessToken: token
		};

		if (newConsentAction === 'ACCEPT') {
			saveUserConsentRequestPayload.consentGroupIdsActions.acceptList = appConfig.consentGroupIds;
		} else {
			saveUserConsentRequestPayload.consentGroupIdsActions.rejectList = appConfig.consentGroupIds;
		}

		if (sendSMS) {
			saveUserConsentRequestPayload.consentIdsActions.acceptList = ['SMS_MESSAGING'];
		}

		const saveUserConsentPromise = requestRedwolfService(
			consentAPI.saveUserConsent(saveUserConsentRequestPayload)
		);

		let updateEmailPreference: any = null;

		if (termsOfServiceStatus !== 'POLICY_CHANGE' && newConsentAction !== 'REJECT') {
			updateEmailPreference = requestRedwolfService(authAPI.updateEmailPreference(token, true));
		} else {
			updateEmailPreference = Promise.resolve('NO_USER_UPDATE');
		}

		Promise.all([updateEmailPreference, saveUserConsentPromise])
			.then(([emailOptinResponse, saveUserConsentResponse]) => {
				setIsSpinnerLoading(false);
				if (emailOptinResponse !== 'NO_USER_UPDATE') {
					if (
						emailOptinResponse.constructor.$metadata$.simpleName === 'Success' &&
						emailOptinResponse.data
					) {
						dispatch(setEmailPreferenceStatus(emailOptinResponse.data.emailOptin));
					} else {
						showTechnicalErrorMessage();
						Sentry.captureException(new Error('updateEmailPreference:' + emailOptinResponse));
					}
				}

				const consentStatus = saveUserConsentResponse.constructor.$metadata$.simpleName;
				if (consentStatus === 'Success') {
					const toSStatus = newConsentAction === 'ACCEPT' ? 'ACTIVE' : 'WITHDRAWN';
					dispatch(setTermsOfServiceStatus(toSStatus));

					if (newConsentAction === 'REJECT') {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_TOS', { item_title: 'Cancel' });
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_TOS_DECLINED');

						setIsSpinnerLoading(true);
						handleUserLogout('home');
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_TOS', { item_title: 'Continue' });
						userRedirection(redirectURLFromQueryString);
					}
				} else {
					showTechnicalErrorMessage();

					Sentry.captureException(new Error('saveUserConsent:' + saveUserConsentResponse));
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
				transaction && transaction.end();
				showTechnicalErrorMessage();
			});
	};

	let consentIAgreeText = '';
	let consentByContinueText = '';

	if (consentMessage.checkBoxText) {
		const consentCheckboxTexts = consentMessage.checkBoxText.split(/\r?\n/);

		if (consentCheckboxTexts.length === 1) {
			consentIAgreeText = consentCheckboxTexts[0];
		} else if (consentCheckboxTexts.length === 2) {
			consentIAgreeText = consentCheckboxTexts[0];
			consentByContinueText = consentCheckboxTexts[1];
		}
	}

	return (
		<PublicContainer>
			<div className="component form-create scroll-component scroll-service-section term-service tab-block">
				<div className="tos-tab">
					{/* New UI start here */}
					<Accordion collapsible role="list" className="accordion-main-container">
						<AccordionItem role="listitem" className="accordion-list">
							<AccordionButton
								className="accordion-title"
								onClick={() => {
									setSelectedTab('TERMS_OF_USE');
								}}>
								<div className="accordition-title-name" id="accordition-title-info1">
									Terms of Use
								</div>
							</AccordionButton>
							<div className="form-checkbox CheckBox">
								<input
									type="checkbox"
									aria-labelledby="accordition-title-info1"
									id="accordion-checkbox1"
									checked={acceptTerms}
									onChange={(event) => handleSetTermsofUse(event.target.checked)}
								/>
								<label htmlFor="accordion-checkbox1" className="checkbox-label"></label>
							</div>

							<AccordionPanel className="accordion-details" tabIndex={0}>
								{selectedTab === 'TERMS_OF_USE' && (
									<div className="scroll-content">
										{consentMessage.termsOfServiceText && (
											<pre style={{ textAlign: 'justify' }}>
												{consentMessage.termsOfServiceText}
											</pre>
										)}
									</div>
								)}
							</AccordionPanel>
						</AccordionItem>
						<AccordionItem role="listitem" className="accordion-list">
							<AccordionButton
								className="accordion-title"
								onClick={() => {
									setSelectedTab('PRIVACY_POLICY');
								}}>
								<div className="accordition-title-name" id="accordition-title-info2">
									Privacy Policy
								</div>
							</AccordionButton>
							<div className="form-checkbox CheckBox">
								<input
									type="checkbox"
									aria-labelledby="accordition-title-info2"
									id="accordion-checkbox2"
									checked={acceptTermsPolicy}
									onChange={(event) => handleSetTerms(event.target.checked)}
								/>
								<label htmlFor="accordion-checkbox2" className="checkbox-label"></label>
							</div>

							<AccordionPanel className="accordion-details" tabIndex={0}>
								{selectedTab === 'PRIVACY_POLICY' && (
									<div className="scroll-content">
										{consentMessage.privacyPolicyText && (
											<pre style={{ textAlign: 'justify' }}>{consentMessage.privacyPolicyText}</pre>
										)}
									</div>
								)}
							</AccordionPanel>
						</AccordionItem>
					</Accordion>
					{/* New UI end here */}
				</div>

				{termsOfServiceStatus !== 'POLICY_CHANGE' && consentByContinueText && (
					<div className="scroll-label TOS-condition" id="ageCheckbox">
						<p>{consentByContinueText}</p>
					</div>
				)}

				<div className="button-block">
					{!isMobileView && (
						<button
							type="button"
							className="primary-button sc-btn deleteCardBtn desktop-btn"
							data-dismiss="modal"
							onClick={() => setShowDeclineModal(true)}>
							<span>Cancel</span>
						</button>
					)}

					<button
						type="button"
						className="secondary-button sc-btn updateCardBtn"
						onClick={handleContinue}
						disabled={!acceptTerms || !acceptTermsPolicy}>
						<span>Confirm</span>
					</button>

					{isMobileView && (
						<button
							type="button"
							className="primary-button sc-btn deleteCardBtn mobile-btn"
							data-dismiss="modal"
							onClick={() => setShowDeclineModal(true)}>
							<span>Cancel</span>
						</button>
					)}
				</div>

				<Modal
					containerId="tosDecline"
					containerClasses="tos-modal"
					show={showDeclineModal}
					onHide={handleModalClose}
					heading="Declining Terms of Service">
					<div>
						<div className="decline-container">
							<p>
								By declining VIZIO Account purchase Terms of Service you will be unable to purchase
								content or subscriptions through VIZIO.
							</p>
							<p>Are you sure you wish to decline?</p>
						</div>
						<form className="edit-modal-form">
							<div className="tos-bottom decline-tos-bottom">
								<div className="button-field-group-terms">
									{!isMobileView && (
										<button
											type="button"
											className="secondary-button desktop-btn"
											data-dismiss="modal"
											onClick={handleModalClose}>
											<span>No</span>
										</button>
									)}

									<button
										type="button"
										className="primary-button delete-btn"
										onClick={handleDecline}>
										<span>Yes</span>
									</button>

									{isMobileView && (
										<button
											type="button"
											className="secondary-button mobile-btn"
											data-dismiss="modal"
											onClick={handleModalClose}>
											<span>No</span>
										</button>
									)}
								</div>
							</div>
						</form>
					</div>
				</Modal>

				{isSpinnerLoading && <Spinner />}
			</div>
		</PublicContainer>
	);
};

export default TermsOfService;
